// FILE: FeaturedArticleEndcap.jsx
import React from "react";
import styles from "../../../styles/pages/about/FeaturedArticleEndcap.module.scss";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, BookOpen } from "lucide-react";

export default function FeaturedArticleEndcap({
  article,
  accentFrom = "#8b5cf6",
  accentTo = "#22d3ee",
  className = "",
}) {
  // Guard if no article exists
  if (!article) return null;

  const href = `/article/${encodeURIComponent(article.id)}`;

  const title = article.title || "Featured article";
  const dek = article.summary || "";

  const dateLabel = article.date
    ? new Date(article.date).toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
      })
    : "";

  const readingTime = article.read ? `${article.read} min read` : "";

  const image = article.heroImage;
  const tags = Array.isArray(article.tags) ? article.tags : [];

  return (
    <section
      className={`${styles.root} ${className}`}
      style={{
        "--accent-from": accentFrom,
        "--accent-to": accentTo,
      }}
    >
      <div className={styles.card}>
        <div className={styles.gridBg} aria-hidden />

        {/* Text column */}
        <div className={styles.textCol}>
          <span className={styles.kicker}>
            <BookOpen className={styles.kickerIcon} />
            Featured article
          </span>

          <h2 className={styles.title}>{title}</h2>
          <p className={styles.dek}>{dek}</p>

          <div className={styles.meta}>
            {dateLabel && (
              <span className={styles.metaItem}>
                <Calendar size={14} /> {dateLabel}
              </span>
            )}
            {readingTime && (
              <span className={styles.metaItem}>
                <Clock size={14} /> {readingTime}
              </span>
            )}
          </div>

          {tags.length > 0 && (
            <div className={styles.tags}>
              {tags.slice(0, 5).map((t, i) => (
                <span key={i} className={styles.tag}>
                  {t}
                </span>
              ))}
            </div>
          )}

          <div className={styles.actions}>
            <Link to={href} className={styles.primaryBtn}>
              Read the article
              <ArrowRight className={styles.arrow} />
            </Link>

            {/* Optional secondary */}
            <Link to="/article" className={styles.secondaryBtn}>
              View all articles
            </Link>
          </div>
        </div>

        {/* Visual column (clickable) */}
        <Link to={href} className={styles.visualCol} aria-label={`Read: ${title}`}>
          <div className={styles.visualWrap}>
            <div className={styles.vignette} aria-hidden />

            {image ? (
              <img
                src={image}
                alt={title}
                className={styles.cover}
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  // if broken url, just hide image and show faux scene
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <div className={styles.fauxScene} aria-hidden>
                <span className={styles.spark} style={{ "--d": "0s" }} />
                <span className={styles.spark} style={{ "--d": ".3s" }} />
                <span className={styles.spark} style={{ "--d": ".6s" }} />
                <div className={styles.sceneGrid} />
              </div>
            )}
          </div>
        </Link>

        {/* subtle edge ring */}
        <div className={styles.ring} aria-hidden />
      </div>

      {/* Footer micro-CTA row */}
      <div className={styles.footerCtas}>
        <Link to={href} className={styles.footerLink}>
          Read now
        </Link>
        <span className={styles.dot}>•</span>
        <Link to="" className={styles.footerLink}>
          Get in touch
        </Link>
        <span className={styles.dot}>•</span>
        <Link to="/article" className={styles.footerLink}>
          Browse articles
        </Link>
      </div>
    </section>
  );
}
